import { useEffect, useState } from 'react';
import { useAuthContext } from '../hooks/useAuthContext';
import { useLogout } from '../hooks/useLogout';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db } from '../firebase/config';
import { Link } from 'react-router-dom';
import { FaShoppingBag, FaBoxOpen, FaHeart, FaEdit, FaSave, FaTimes } from 'react-icons/fa';
import './MyAccount.css';

export default function MyAccount() {
  const { user } = useAuthContext();
  const { logout } = useLogout();
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedUserData, setEditedUserData] = useState(null);
  const [profileImage, setProfileImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

// Inside useEffect
useEffect(() => {
  const fetchUserData = async () => {
    if (user) {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        setUserData(data);
        setEditedUserData({ ...data }); // Ensure all fields are editable
        setLoading(false);
      } else {
        console.error("User document does not exist.");
      }
    }
  };
  fetchUserData();
}, [user]);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedUserData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileImage(file);
      
      // Create image preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveProfile = async () => {
    try {
      // Upload profile image if a new one is selected
      let photoURL = editedUserData.photoURL;
      if (profileImage) {
        const storage = getStorage();
        const storageRef = ref(storage, `profileImages/${user.uid}`);
        
        // Upload the image
        await uploadBytes(storageRef, profileImage);
        
        // Get the download URL
        photoURL = await getDownloadURL(storageRef);
      }

      // Update Firestore document
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        ...editedUserData,
        photoURL: photoURL || editedUserData.photoURL
      });

      // Update local state
      setUserData({
        ...editedUserData,
        photoURL: photoURL || editedUserData.photoURL
      });

      // Reset edit mode
      setIsEditing(false);
      setProfileImage(null);
      setImagePreview(null);
    } catch (error) {
      console.error("Error updating profile:", error);
      alert("Failed to update profile. Please try again.");
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedUserData({ ...userData });
    setProfileImage(null);
    setImagePreview(null);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner-overlay">
        <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <main>
      <br /><br /><br />
          <div className="myaccount-container">
      <div className="myaccount-header">
        <div className="user-info">
        <div className="user-greeting">
            {isEditing ? (
              <div className="edit-name-container">
              <input
                type="text"
                name="displayName"
                value={editedUserData.displayName}
                onChange={handleInputChange}
                className="edit-name-input"
                placeholder="Display Name"
              />
            </div>
            ) : (
              <h1>{getGreeting()}, {userData.displayName}</h1>
            )}
          </div>

          <div className="profile-image-container">
            <img
              src={imagePreview || userData.photoURL || '/default-avatar.jpg'}
              alt={userData.displayName}
              className="profile-image"
            />
            {isEditing && (
              <div className="image-upload-overlay">
                <label htmlFor="profile-image-upload" className="image-upload-label">
                  Change Photo
                  <input
                    type="file"
                    id="profile-image-upload"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="image-upload-input"
                  />
                </label>
              </div>
            )}
            
            {/* Edit toggle */}
            <div className="edit-profile-toggle">
              {!isEditing ? (
                <button 
                  onClick={() => setIsEditing(true)} 
                  className="edit-profile-button"
                >
                  <FaEdit /> Edit Profile
                </button>
              ) : (
                <div className="edit-actions">
                  <button 
                    onClick={handleSaveProfile} 
                    className="save-profile-button"
                  >
                    <FaSave /> Save
                  </button>
                  <button 
                    onClick={handleCancelEdit} 
                    className="cancel-edit-button"
                  >
                    <FaTimes /> Cancel
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Additional user details for editing */}
      <div className="user-details">
  {!isEditing ? (
    <>
      <p><strong>Email:</strong> {userData.email}</p>
      <p><strong>Phone Number:</strong> {userData.phoneNumber || 'Not provided'}</p>
    </>
  ) : (
    <div className="user-details-edit">
      <div className="edit-field">
        <label htmlFor="email">Email</label>
        <input
          type="email"
          name="email"
          id="email"
          value={editedUserData.email}
          onChange={handleInputChange}
          className="edit-input"
          placeholder="Email"
          disabled // Prevent editing email
        />
      </div>
      
      <div className="edit-field">
        <label htmlFor="phoneNumber">Phone Number</label>
        <input
          type="tel"
          name="phoneNumber"
          id="phoneNumber"
          value={editedUserData.phoneNumber || ''}
          onChange={handleInputChange}
          className="edit-input"
          placeholder="Phone Number"
        />
      </div>
    </div>
  )}
</div>
      
      <div className="myaccount-links">
        <Link to="/mybag" className="account-link">
          <FaShoppingBag />
          <span>My Bag</span>
        </Link>
        <Link to="/myorders" className="account-link">
          <FaBoxOpen />
          <span>My Orders</span>
        </Link>
        <Link to="/saved-items" className="account-link">
          <FaHeart />
          <span>Saved Items</span>
        </Link>
      </div>
      
      {!isEditing && (
        <button onClick={logout} className="logout-button">Log Out</button>
      )}
    </div>
    </main>
  );
}